import IndigoApi from 'communication/IndigoApi';
import { ChannelDestination } from 'communication/Channel';
import {
    createStreamStatusStreamingAction,
    createStreamStatusPendingAction,
    createStreamStatusHandleStreamErrorAction,
    createStreamStatusHandleErrorAction,
    createStreamStatusStoppedAction,
} from 'streamsState/store/streamStatuses/actions';
import {
    selectAllStreamKeys,
    selectStreamDataStreamState,
} from 'streamsState/store/streamStatuses/selectors';
import { IndigoStore } from 'store/store';
import IndigoMalformedMessage from 'communication/IndigoMalformedMessage';
import Handler from 'communication/Handler';
import {
    genericStreamError,
    malformedMessageError,
    StreamError,
} from 'streamsState/store/streamStatuses/StreamErrorState';
import {
    ActionsDeferredDispatcher,
    ActionsDispatcher,
    ActionsImmediateDispatcher,
} from 'store/ActionsDispatcher';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { using as usingStatement } from 'using-statement';
import { StreamRequestBatch } from 'communication/StreamRequestBatch';
import { RequestBatchSanitizer } from 'communication/streaming/SockJSStreamingService';

interface StreamDataServiceOptions {
    indigoApi: IndigoApi;
    loggingService: LoggingService;
}

export interface StreamDataHandlers<Data> {
    onPending?: (streamKey: string) => void;
    onMessage: (streamKey: string, data: Data) => void;
    onError: (streamKey: string, error: StreamError) => void;
}

export default class StreamDataService {
    private indigoApi: IndigoApi;
    private logger: Logger;
    private loggingService: LoggingService;
    public store: IndigoStore | undefined;

    public constructor(options: StreamDataServiceOptions) {
        this.indigoApi = options.indigoApi;
        this.logger = buildLogger('StreamDataService', options.loggingService);
        this.indigoApi.subscribeUnhandledMalformedMessage(
            (message: IndigoMalformedMessage) =>
                this.onUnhandledMalformedMessage(message)
        );
        this.loggingService = options.loggingService;
    }

    public startAndWatchStream<RequestPayload, Data>(
        streamKey: string,
        channelDestination: ChannelDestination,
        handlers: StreamDataHandlers<Data>,
        channelId?: number,
        dispatcher: ActionsDispatcher = new ActionsImmediateDispatcher(
            this.store!
        )
    ) {
        if (!this.store) {
            throw new Error('store must be set');
        }

        let streaming = false;

        const messageHandler: Handler<Data> = (
            data: Data,
            isMalformedMessage?: boolean
        ) => {
            if (!streaming) {
                streaming = true;
                this.store!.dispatch(
                    createStreamStatusStreamingAction(streamKey)
                );
            }

            if (isMalformedMessage) {
                const err = malformedMessageError(
                    data as IndigoMalformedMessage
                );
                this.store!.dispatch(
                    createStreamStatusHandleStreamErrorAction(streamKey, err)
                );
                handlers.onError(streamKey, err);
            } else {
                handlers.onMessage(streamKey, data);
            }
        };

        return async (payload: RequestPayload): Promise<void> => {
            try {
                channelId = this.indigoApi.startStreamRequest(
                    payload,
                    channelDestination,
                    messageHandler,
                    channelId
                ).id;

                dispatcher.dispatch(
                    createStreamStatusPendingAction(
                        streamKey,
                        channelId,
                        channelDestination
                    )
                );

                if (handlers.onPending) {
                    handlers.onPending(streamKey);
                }
            } catch (error) {
                const streamError = genericStreamError(error as Error);
                streaming = false;
                this.store!.dispatch(
                    createStreamStatusHandleStreamErrorAction(
                        streamKey,
                        streamError
                    )
                );
                handlers.onError(streamKey, streamError);
            }
        };
    }

    public async stopStream(
        streamKey: string,
        dispatcher: ActionsDispatcher = new ActionsImmediateDispatcher(
            this.store!
        ),
        removeChannelListener = true
    ) {
        const streamState = selectStreamDataStreamState(
            this.store!.getState(),
            streamKey
        );

        if (streamState && streamState.channelId) {
            this.logger.debug('stopping stream', {
                ...streamState,
            });

            dispatcher.dispatch(createStreamStatusStoppedAction(streamKey));

            this.indigoApi.stopDataStream(
                streamState.channelId,
                streamState.channelDestination!,
                removeChannelListener
            );
        } else {
            this.logger.debug('Attempt to stop stream with no channelId', {
                ...streamState,
            });
        }
    }

    public async stopAllStreams() {
        usingStatement(
            new ActionsDeferredDispatcher(this.store!),
            (dispatcher) => {
                for (const streamKey of selectAllStreamKeys(
                    this.store!.getState()
                )) {
                    this.stopStream(streamKey, dispatcher);
                }
            }
        );
    }
    public beginRequestBatch(): StreamRequestBatch {
        const batch = new StreamRequestBatch(
            this.indigoApi,
            this.loggingService
        );
        this.logger.debug(
            'Starting stream request batch ' + batch.correlationId
        );
        return batch;
    }

    private onUnhandledMalformedMessage(message: IndigoMalformedMessage) {
        this.store &&
            this.store.dispatch(
                createStreamStatusHandleErrorAction(
                    malformedMessageError(message)
                )
            );
    }

    public registerRequestBatchSanitizer(
        channelDestination: ChannelDestination,
        sanitizer: RequestBatchSanitizer
    ) {
        this.indigoApi.registerRequestBatchSanitizer(
            channelDestination,
            sanitizer
        );
    }
}
